import {
  Box,
  Card,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  Business,
  Engineering,
  Warning,
  ArrowForward,
  Notifications,
  TrendingUp,
  TrendingDown,
  PriorityHigh,
  Close,
  Payments,
  PersonAdd,
  People,
  AccountBalance,
  ShowChart,
  Api,
  Schedule,
  CalendarMonth,
  ExpandMore,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  Line,
  ComposedChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useMemo, useState } from 'react';

const formatNumber = (num) => Number(num || 0).toLocaleString('ko-KR');
const EMPTY_ARRAY = [];

const TOP_REVENUE_SHARE_PIE_COLORS = [
  '#137fec',
  '#0f766e',
  '#7c3aed',
  '#ea580c',
  '#db2777',
  '#0891b2',
  '#65a30d',
  '#64748b',
];

/** 기기 유형(태블릿·모바일·웹) — 막대·파이 공통 */
const DEVICE_TYPE_CHART_COLORS = ['#7c3aed', '#137fec', '#0d9488'];

const GetCompanyUsageStatus = (pct) => {
  const n = Number(pct) || 0;
  if (n >= 70) {
    return {
      label: '양호',
      barFill: '#22c55e',
      pillSx: {
        bgcolor: 'rgba(34,197,94,0.12)',
        color: '#15803d',
        border: '1px solid rgba(34,197,94,0.25)',
      },
    };
  }
  if (n >= 30) {
    return {
      label: '주의',
      barFill: '#eab308',
      pillSx: {
        bgcolor: 'rgba(255,183,77,0.18)',
        color: '#E68A00',
        border: '1px solid rgba(255,183,77,0.25)',
      },
    };
  }
  return {
    label: '저활용',
    barFill: '#ef4444',
    pillSx: {
      bgcolor: 'rgba(231,57,8,0.08)',
      color: '#E73908',
      border: '1px solid rgba(231,57,8,0.12)',
    },
  };
};

/** 회사별 사용 현황 — 활용률 높은 순 */
const BuildCompanyUsageChartRows = (rows) => {
  return [...(Array.isArray(rows) ? rows : [])]
    .map((r) => {
      const name = String(r.companyName ?? '-').trim() || '-';
      const rate = Number(r.usageRatePercent) || 0;
      const contractMemberCount = Number(r.contractMemberCount) || 0;
      const activeUserCount = Number(r.activeUserCount) || 0;
      const actionCount = Number(r.actionCount) || 0;
      const status = GetCompanyUsageStatus(rate);
      return {
        coCd: r.coCd,
        companyFullName: name,
        usageRatePercent: rate,
        contractMemberCount,
        activeUserCount,
        actionCount,
        statusLabel: status.label,
        barFill: status.barFill,
        statusPillSx: status.pillSx,
        memberSummary:
          contractMemberCount > 0
            ? `활성 ${formatNumber(activeUserCount)}명 / 계약 ${formatNumber(contractMemberCount)}명`
            : `활성 ${formatNumber(activeUserCount)}명`,
      };
    })
    .sort((a, b) => b.usageRatePercent - a.usageRatePercent);
};

const BuildCompanyUsageOverview = (chartRows) => {
  const list = Array.isArray(chartRows) ? chartRows : [];
  if (!list.length) {
    return {
      total: 0,
      avgRate: 0,
      goodCount: 0,
      warnCount: 0,
      lowCount: 0,
    };
  }
  const sum = list.reduce((acc, r) => acc + (Number(r.usageRatePercent) || 0), 0);
  return {
    total: list.length,
    avgRate: Math.round(sum / list.length),
    goodCount: list.filter((r) => r.usageRatePercent >= 70).length,
    warnCount: list.filter(
      (r) => r.usageRatePercent >= 30 && r.usageRatePercent < 70
    ).length,
    lowCount: list.filter((r) => r.usageRatePercent < 30).length,
  };
};

const COMPANY_USAGE_ZONE_BANDS = [
  { widthPct: 30, bgcolor: 'rgba(239,68,68,0.14)', label: '저활용' },
  { widthPct: 40, bgcolor: 'rgba(234,179,8,0.14)', label: '주의' },
  { widthPct: 30, bgcolor: 'rgba(34,197,94,0.14)', label: '양호' },
];

/** 화면별 이용 TOP 5 — 1위 대비 상대 막대 너비(%) */
const BuildTopMenuFeatureBarRows = (rows) => {
  const list = [...(Array.isArray(rows) ? rows : [])].sort(
    (a, b) =>
      (Number(a.rank) || 99) - (Number(b.rank) || 99) ||
      (Number(b.usageCount) || 0) - (Number(a.usageCount) || 0)
  );
  const max = Math.max(
    1,
    ...list.map((r) => Number(r.usageCount) || 0)
  );
  return list.map((r) => {
    const count = Number(r.usageCount) || 0;
    return {
      rank: Number(r.rank) || 0,
      featureName: String(r.featureName ?? '-').trim() || '-',
      menuId: r.menuId,
      usageCount: count,
      barPct: Math.round((count / max) * 100),
    };
  });
};

const TOP_MENU_FEATURE_BAR_ROW_PX = 40;

const FormatSessionDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total < 60) return `${total}초`;
  const minutes = Math.floor(total / 60);
  const remSec = total % 60;
  if (minutes < 60) {
    return remSec > 0 ? `${minutes}분 ${remSec}초` : `${minutes}분`;
  }
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return remMin > 0 ? `${hours}시간 ${remMin}분` : `${hours}시간`;
};

const BuildSessionDurationChartRows = (comparison) => {
  if (!comparison) return [];
  const prior = Number(comparison.priorAvgDurationSeconds) || 0;
  const current = Number(comparison.currentAvgDurationSeconds) || 0;
  return [
    {
      label: '직전 30일',
      durationSeconds: prior,
      durationLabel: FormatSessionDuration(prior),
      fill: '#94a3b8',
    },
    {
      label: '최근 30일',
      durationSeconds: current,
      durationLabel: FormatSessionDuration(current),
      fill: '#137fec',
    },
  ];
};

const GetBounceRateStyle = (pct) => {
  const n = Number(pct) || 0;
  if (n >= 70) {
    return {
      bgcolor: 'rgba(231,57,8,0.08)',
      color: '#E73908',
      border: '1px solid rgba(231,57,8,0.12)',
    };
  }
  if (n >= 40) {
    return {
      bgcolor: 'rgba(255,183,77,0.18)',
      color: '#E68A00',
      border: '1px solid rgba(255,183,77,0.25)',
    };
  }
  return {
    bgcolor: 'rgba(34,197,94,0.12)',
    color: '#15803d',
    border: '1px solid rgba(34,197,94,0.25)',
  };
};

function TopMenuFeatureTop5Card({
  title,
  subtitle,
  accentColor,
  headerBg,
  rows,
  emptyMessage,
}) {
  const barRows = useMemo(() => BuildTopMenuFeatureBarRows(rows), [rows]);

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(231,237,243,1)',
        boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: '1px solid rgba(231,237,243,1)',
          bgcolor: headerBg,
        }}
      >
        <Typography sx={{ fontSize: 17, fontWeight: 900 }}>{title}</Typography>
        <Typography
          sx={{
            mt: 0.35,
            fontSize: 12,
            color: 'text.secondary',
            fontWeight: 600,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          p: 2,
          minHeight: 220,
        }}
      >
        {barRows.length === 0 ? (
          <Box
            sx={{
              height: 180,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {emptyMessage}
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            {barRows.map((row) => (
              <Tooltip
                key={`${row.menuId || row.featureName}-${row.rank}`}
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
                      {row.featureName}
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      이용: {formatNumber(row.usageCount)}건
                    </Typography>
                  </Box>
                }
                placement="top"
                arrow
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '28px minmax(72px, 32%) 1fr 52px',
                      sm: '32px minmax(88px, 30%) 1fr 56px',
                    },
                    columnGap: { xs: 0.75, sm: 1.25 },
                    alignItems: 'center',
                    minHeight: TOP_MENU_FEATURE_BAR_ROW_PX,
                    py: 0.35,
                    cursor: 'default',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: accentColor,
                      textAlign: 'center',
                    }}
                  >
                    {row.rank}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 11, sm: 12 },
                      fontWeight: 800,
                      lineHeight: 1.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'break-all',
                    }}
                  >
                    {row.featureName}
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      height: 22,
                      borderRadius: 1,
                      bgcolor: 'rgba(148,163,184,0.22)',
                      overflow: 'hidden',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.min(100, Math.max(0, row.barPct))}%`,
                        height: '100%',
                        bgcolor: accentColor,
                        borderRadius: 1,
                        transition: 'width 0.25s ease',
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      textAlign: 'right',
                    }}
                  >
                    {formatNumber(row.usageCount)}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
}

const formatMoMGrowth = (rate) => {
  const n = Number(rate);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0%';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
};

/** dailyActiveUsers — dateLabel 정렬 후 최근 이틀로 전일 대비 DAU 증감률(%) */
const ComputeDauChangeVsPriorDay = (rows) => {
  if (!Array.isArray(rows) || rows.length < 2) {
    return { changeRate: NaN, todayCount: 0, priorCount: 0 };
  }
  const mapped = rows
    .map((r) => ({
      label: String(r?.dateLabel ?? r?.DateLabel ?? '').trim(),
      count: Number(r?.activeUserCount ?? r?.ActiveUserCount ?? 0),
    }))
    .filter((r) => r.label);

  if (mapped.length < 2) {
    return { changeRate: NaN, todayCount: 0, priorCount: 0 };
  }

  mapped.sort((a, b) => a.label.localeCompare(b.label));
  const prior = mapped[mapped.length - 2].count;
  const today = mapped[mapped.length - 1].count;
  if (!Number.isFinite(prior) || !Number.isFinite(today)) {
    return { changeRate: NaN, todayCount: today, priorCount: prior };
  }
  if (prior === 0) {
    return {
      changeRate: today > 0 ? 100 : 0,
      todayCount: today,
      priorCount: prior,
    };
  }
  return {
    changeRate: ((today - prior) / prior) * 100,
    todayCount: today,
    priorCount: prior,
  };
};

/** dateLabel 오름차순 후, 각 일자 기준 당일 포함 최대 7일 구간 단순 이동평균(activeUserCountMa7) */
const BuildDailyActiveUsersWithMa7 = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const sorted = [...rows]
    .map((r) => ({
      ...r,
      dateLabel: String(r?.dateLabel ?? r?.DateLabel ?? '').trim(),
      activeUserCount: Number(r?.activeUserCount ?? r?.ActiveUserCount ?? 0),
    }))
    .filter((r) => r.dateLabel)
    .sort((a, b) => a.dateLabel.localeCompare(b.dateLabel));

  return sorted.map((row, i) => {
    const from = Math.max(0, i - 6);
    const windowRows = sorted.slice(from, i + 1);
    const sum = windowRows.reduce(
      (s, x) =>
        s + (Number.isFinite(x.activeUserCount) ? x.activeUserCount : 0),
      0
    );
    const ma7 = windowRows.length > 0 ? sum / windowRows.length : 0;
    return {
      ...row,
      activeUserCountMa7: Math.round(ma7 * 10) / 10,
    };
  });
};

const formatSignedAmount = (amount) => {
  const n = Number(amount || 0);
  if (n === 0) return '0원';
  const sign = n > 0 ? '+' : '-';
  return `${sign}${formatNumber(Math.abs(n))}원`;
};

/** API 미연동 시 최근 12개월 축만 표시 (값 0) — GetMaiBasicR1_MonthlyContractStats와 동일 기간 */
const BuildLast12MonthPlaceholders = () => {
  const out = [];
  const now = new Date();
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const yearMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = `${d.getFullYear()}년 ${String(d.getMonth() + 1).padStart(2, '0')}월`;
    out.push({
      monthLabel,
      yearMonth,
      companyCount: 0,
      monthlyFeeTotal: 0,
    });
  }
  return out;
};

const defaultRiskAccounts = [
  { companyName: 'BuildRight Inc.', lastLoginText: '22일 전', level: '고위험' },
  { companyName: 'SafeStruct LLC', lastLoginText: '16일 전', level: '중위험' },
  { companyName: 'Apex Towers', lastLoginText: '15일 전', level: '중위험' },
  { companyName: 'Metro Safety', lastLoginText: '28일 전', level: '고위험' },
];

const defaultAuditLogs = [
  {
    user: 'John Smith',
    action: '신규 사용자 5명 추가',
    company: 'BuildRight Inc.',
    dateText: '2023년 10월 24일 오전 10:30',
    status: '성공',
  },
  {
    user: 'Sarah Connor',
    action: '안전 정책 #12 업데이트',
    company: 'SkyHigh Constr.',
    dateText: '2023년 10월 24일 오전 09:15',
    status: '성공',
  },
  {
    user: 'Michael Ross',
    action: '로그인 시도 실패',
    company: 'SafeStruct LLC',
    dateText: '2023년 10월 23일 오후 04:45',
    status: '경고',
  },
];

const tryParseDate = (value) => {
  if (!value) return null;

  // 1) Date/epoch 처리
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const s = String(value).trim();
  if (!s) return null;

  // 2) ISO/브라우저 파서 시도
  const iso = new Date(s);
  if (!Number.isNaN(iso.getTime())) return iso;

  // 3) YYYY-MM-DD HH:mm:ss / YYYY-MM-DD
  const m1 = s.match(
    /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (m1) {
    const [, y, mo, d, hh = '0', mm = '0', ss = '0'] = m1;
    const dt = new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss)
    );
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // 4) YYYYMMDDHHmmss / YYYYMMDD
  const m2 = s.match(/^(\d{4})(\d{2})(\d{2})(\d{2})?(\d{2})?(\d{2})?$/);
  if (m2) {
    const [, y, mo, d, hh = '0', mm = '0', ss = '0'] = m2;
    const dt = new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss)
    );
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // 5) 2023년 10월 24일 오전 10:30
  const m3 = s.match(
    /^(\d{4})년\s*(\d{1,2})월\s*(\d{1,2})일(?:\s*(오전|오후))?\s*(\d{1,2}):(\d{2})$/
  );
  if (m3) {
    const [, y, mo, d, ampm, hh, mm] = m3;
    let hour = Number(hh);
    if (ampm === '오후' && hour < 12) hour += 12;
    if (ampm === '오전' && hour === 12) hour = 0;

    const dt = new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      hour,
      Number(mm),
      0
    );
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  return null;
};

const pillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: 1,
  fontSize: '0.75rem',
  fontWeight: 700,
  lineHeight: 1.2,
};

function DashboardSection({
  title,
  description,
  children,
  defaultExpanded = true,
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 2,
        border: '1px solid rgba(231,237,243,1)',
        boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
        bgcolor: 'background.paper',
        '&:before': { display: 'none' },
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMore sx={{ fontSize: 22, color: 'text.secondary' }} />
        }
        sx={{
          px: 2,
          py: 0.5,
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            my: 1.25,
            alignItems: 'flex-start',
          },
        }}
      >
        <Box
          sx={{
            pl: 1.5,
            borderLeft: '4px solid #137fec',
            width: '100%',
            pr: 1,
          }}
        >
          <Typography sx={{ fontSize: '1.12rem', fontWeight: 900 }}>
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 0.35,
              fontSize: 13,
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            {description}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pb: 2.5, pt: 0 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

const kpiCardSx = {
  p: 2.5,
  borderRadius: 3,
  border: '1px solid rgba(231,237,243,1)',
  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
};

function MAI_BASIC(props) {
  const { periodDays, setPeriodDays, dashboard } = props;

  const totalCompanyCount = dashboard?.totalCompanyCount ?? 0;
  const activeSiteCount = dashboard?.activeSiteCount ?? 0;
  const expiringContractCount = dashboard?.expiringContractCount ?? 0;
  const dailyActiveUsers = dashboard?.dailyActiveUsers ?? EMPTY_ARRAY;
  const riskAccounts = dashboard?.riskAccounts ?? EMPTY_ARRAY;
  const recentAuditLogs = dashboard?.recentAuditLogs ?? EMPTY_ARRAY;
  const monthlyContractTrend = dashboard?.monthlyContractTrend ?? EMPTY_ARRAY;
  const mrrChangeRate = dashboard?.mrrChangeRate ?? 0;
  const currentMonthNewContractMonthlyFeeSum =
    dashboard?.currentMonthNewContractMonthlyFeeSum ?? 0;
  const mrrDeltaAmount = dashboard?.mrrDeltaAmount ?? 0;
  const totalOverallRevenue = dashboard?.totalOverallRevenue ?? 0;
  const yearToDateTotalRevenue = dashboard?.yearToDateTotalRevenue ?? 0;
  const totalContractWorkerCount = dashboard?.totalContractWorkerCount ?? 0;
  const newSignupsLast7Days = dashboard?.newSignupsLast7Days ?? 0;
  const newSignupsChangeRate = dashboard?.newSignupsChangeRate;
  const currentConcurrentUsers = dashboard?.currentConcurrentUsers ?? 0;
  const apiCallCountLast24Hours = dashboard?.apiCallCountLast24Hours ?? 0;
  const expiringSoonMonthlyFeeSum = dashboard?.expiringSoonMonthlyFeeSum ?? 0;
  const revenueYoYChangeRate = dashboard?.revenueYoYChangeRate;
  const revenueYoYCurrentAmount = dashboard?.revenueYoYCurrentAmount ?? 0;
  const revenueYoYPriorAmount = dashboard?.revenueYoYPriorAmount ?? 0;
  const topCustomerRevenueShares =
    dashboard?.topCustomerRevenueShares ?? EMPTY_ARRAY;
  const trafficByWeekday = dashboard?.trafficByWeekday ?? EMPTY_ARRAY;
  const trafficByHour = dashboard?.trafficByHour ?? EMPTY_ARRAY;
  const deviceTypeUsages = dashboard?.deviceTypeUsages ?? EMPTY_ARRAY;
  const topActiveCompanies = dashboard?.topActiveCompanies ?? EMPTY_ARRAY;
  const lowUsageCompanies = dashboard?.lowUsageCompanies ?? EMPTY_ARRAY;
  const companyUsageRates = dashboard?.companyUsageRates ?? EMPTY_ARRAY;
  const topMenuWeb = dashboard?.topMenuWeb ?? EMPTY_ARRAY;
  const sessionDurationComparison =
    dashboard?.sessionDurationComparison ?? null;
  const pageBounceRates = dashboard?.pageBounceRates ?? EMPTY_ARRAY;

  const sessionDurationChartRows = useMemo(
    () => BuildSessionDurationChartRows(sessionDurationComparison),
    [sessionDurationComparison]
  );

  const sessionDurationChangeRate =
    sessionDurationComparison?.changeRatePercent ?? null;

  const companyUsageChartRows = useMemo(
    () => BuildCompanyUsageChartRows(companyUsageRates),
    [companyUsageRates]
  );

  const companyUsageOverview = useMemo(
    () => BuildCompanyUsageOverview(companyUsageChartRows),
    [companyUsageChartRows]
  );

  const dauDayOverDay = useMemo(
    () => ComputeDauChangeVsPriorDay(dailyActiveUsers),
    [dailyActiveUsers]
  );

  const dailyActiveUsersChartData = useMemo(
    () => BuildDailyActiveUsersWithMa7(dailyActiveUsers),
    [dailyActiveUsers]
  );

  const monthlyContractChartData = useMemo(() => {
    if (
      Array.isArray(monthlyContractTrend) &&
      monthlyContractTrend.length > 0
    ) {
      return monthlyContractTrend;
    }
    return BuildLast12MonthPlaceholders();
  }, [monthlyContractTrend]);

  const hasMonthlyContractFromApi =
    Array.isArray(monthlyContractTrend) && monthlyContractTrend.length > 0;

  const topSharePieData = useMemo(() => {
    const rows = Array.isArray(topCustomerRevenueShares)
      ? topCustomerRevenueShares
      : [];
    if (!rows.length) return [];
    const fees = rows.map((r) => Number(r.monthlyFee) || 0);
    const sum = fees.reduce((a, b) => a + b, 0);
    return rows.slice(0, 8).map((r, idx) => {
      const fee = Number(r.monthlyFee) || 0;
      const pct = Number.isFinite(Number(r.sharePercent))
        ? Number(r.sharePercent)
        : sum > 0
          ? (fee / sum) * 100
          : 0;
      const full = String(r.companyName || `항목 ${idx + 1}`);
      return {
        name: full.length > 12 ? `${full.slice(0, 12)}…` : full,
        fullName: full,
        value: Math.round(pct * 10) / 10,
        fee,
      };
    });
  }, [topCustomerRevenueShares]);

  const [riskAllOpen, setRiskAllOpen] = useState(false);
  const [auditAllOpen, setAuditAllOpen] = useState(false);

  const revenueKpiCards = useMemo(() => {
    const deltaUp = mrrDeltaAmount > 0;
    const deltaDown = mrrDeltaAmount < 0;
    const currentYear = new Date().getFullYear();
    return [
      {
        label: '전체 총 매출',
        value: `${formatNumber(totalOverallRevenue)}원`,
        icon: <AccountBalance sx={{ fontSize: 26, color: '#0c4a6e' }} />,
        iconBg: 'rgba(12,74,110,0.10)',
        trendText: '유효 계약만',
        trendIcon: <Payments sx={{ fontSize: 16 }} />,
        trendColor: '#0c4a6e',
        trendBg: 'rgba(12,74,110,0.10)',
        subText:
          '계약 종료된 회사는 제외. 지금 시점 월정액(MRR) 합 — “올해 발생”과 산식이 다릅니다.',
      },
      {
        label: `${currentYear}년 올해 발생 매출 누적`,
        value: `${formatNumber(yearToDateTotalRevenue)}원`,
        icon: <ShowChart sx={{ fontSize: 26, color: '#0369a1' }} />,
        iconBg: 'rgba(3,105,161,0.10)',
        trendText: '결제일·월별 합',
        trendIcon: <CalendarMonth sx={{ fontSize: 16 }} />,
        trendColor: '#0369a1',
        trendBg: 'rgba(3,105,161,0.10)',
        subText: `${currentYear}년 1월~당월, 매월 결제일(PAYMENT_DAY) 기준으로 잡힌 발생 매출을 더한 값입니다.`,
      },
      {
        label: '당월 신규 계약 월정액 합계',
        value: `${formatNumber(currentMonthNewContractMonthlyFeeSum)}원`,
        icon: <Payments sx={{ fontSize: 26, color: '#0f766e' }} />,
        iconBg: 'rgba(15,118,110,0.10)',
        trendText: '당월',
        trendIcon: <CalendarMonth sx={{ fontSize: 16 }} />,
        trendColor: '#0f766e',
        trendBg: 'rgba(15,118,110,0.10)',
        subText: '계약시작일이 당월인 건의 월정액 합',
      },
      {
        label: '총 계약 인원수',
        value: `${formatNumber(totalContractWorkerCount)}명`,
        icon: <People sx={{ fontSize: 26, color: '#2563eb' }} />,
        iconBg: 'rgba(37,99,235,0.10)',
        trendText: '계약 기준',
        trendIcon: <People sx={{ fontSize: 16 }} />,
        trendColor: '#2563eb',
        trendBg: 'rgba(37,99,235,0.10)',
        subText: '전체 합산 인원',
      },
      {
        label: '만료 임박 월정액 합',
        value: `${formatNumber(expiringSoonMonthlyFeeSum)}원`,
        icon: <Schedule sx={{ fontSize: 26, color: '#b45309' }} />,
        iconBg: 'rgba(180,83,9,0.10)',
        trendText: 'MRR 리스크',
        trendIcon: <Warning sx={{ fontSize: 16 }} />,
        trendColor: '#b45309',
        trendBg: 'rgba(180,83,9,0.10)',
        subText: '만료일 ~30일 이내(월정액 합)',
      },
      {
        label: '전년 동월 대비(YoY)',
        value: formatMoMGrowth(revenueYoYChangeRate),
        secondaryLabel: '당월',
        secondaryValue: `${formatNumber(revenueYoYCurrentAmount)}원`,
        icon: <CalendarMonth sx={{ fontSize: 26, color: '#0369a1' }} />,
        iconBg: 'rgba(3,105,161,0.10)',
        trendText: `전년 동월 ${formatNumber(revenueYoYPriorAmount)}원`,
        trendIcon:
          Number.isFinite(Number(revenueYoYChangeRate)) &&
          Number(revenueYoYChangeRate) < 0 ? (
            <TrendingDown sx={{ fontSize: 16 }} />
          ) : (
            <TrendingUp sx={{ fontSize: 16 }} />
          ),
        trendColor:
          Number.isFinite(Number(revenueYoYChangeRate)) &&
          Number(revenueYoYChangeRate) < 0
            ? '#c2410c'
            : Number.isFinite(Number(revenueYoYChangeRate)) &&
                Number(revenueYoYChangeRate) > 0
              ? '#078838'
              : '#64748b',
        trendBg:
          Number.isFinite(Number(revenueYoYChangeRate)) &&
          Number(revenueYoYChangeRate) < 0
            ? 'rgba(194,65,12,0.10)'
            : Number.isFinite(Number(revenueYoYChangeRate)) &&
                Number(revenueYoYChangeRate) > 0
              ? 'rgba(7,136,56,0.10)'
              : 'rgba(100,116,139,0.10)',
        subText: '월 계약금 합·동월 비교',
      },
      {
        label: '전월 대비 변화',
        value: formatSignedAmount(mrrDeltaAmount),
        secondaryLabel: '성장률',
        secondaryValue: formatMoMGrowth(mrrChangeRate),
        icon: deltaDown ? (
          <TrendingDown sx={{ fontSize: 26, color: '#c2410c' }} />
        ) : (
          <TrendingUp sx={{ fontSize: 26, color: '#078838' }} />
        ),
        iconBg: deltaDown
          ? 'rgba(194,65,12,0.10)'
          : deltaUp
            ? 'rgba(7,136,56,0.10)'
            : 'rgba(100,116,139,0.10)',
        trendText: '월정액 기준',
        trendIcon: deltaDown ? (
          <TrendingDown sx={{ fontSize: 16 }} />
        ) : (
          <TrendingUp sx={{ fontSize: 16 }} />
        ),
        trendColor: deltaDown ? '#c2410c' : deltaUp ? '#078838' : '#64748b',
        trendBg: deltaDown
          ? 'rgba(194,65,12,0.10)'
          : deltaUp
            ? 'rgba(7,136,56,0.10)'
            : 'rgba(100,116,139,0.10)',
        subText: '증감액 + 성장률',
      },
    ];
  }, [
    currentMonthNewContractMonthlyFeeSum,
    mrrChangeRate,
    mrrDeltaAmount,
    totalOverallRevenue,
    yearToDateTotalRevenue,
    totalContractWorkerCount,
    expiringSoonMonthlyFeeSum,
    revenueYoYChangeRate,
    revenueYoYCurrentAmount,
    revenueYoYPriorAmount,
  ]);

  const customerKpiCards = useMemo(
    () => [
      {
        label: '총 기업 수',
        value: formatNumber(totalCompanyCount || 0),
        icon: <Business sx={{ fontSize: 26, color: '#137fec' }} />,
        iconBg: 'rgba(19,127,236,0.08)',
        trendText: '전체',
        trendColor: '#64748b',
        trendBg: 'rgba(100,116,139,0.10)',
        subText: '등록 고객사',
        trendIcon: <Business sx={{ fontSize: 16 }} />,
      },
      {
        label: '활성 사업장',
        value: formatNumber(activeSiteCount || 0),
        icon: <Engineering sx={{ fontSize: 26, color: '#7C3AED' }} />,
        iconBg: 'rgba(124,58,237,0.10)',
        trendText: '운영',
        trendColor: '#7C3AED',
        trendBg: 'rgba(124,58,237,0.10)',
        subText: '전체 현장 규모',
        trendIcon: <Engineering sx={{ fontSize: 16 }} />,
      },
      {
        label: '신규 가입 회사',
        value: formatNumber(newSignupsLast7Days || 0),
        ...(Number.isFinite(Number(newSignupsChangeRate))
          ? {
              secondaryLabel: '직전 7일 대비',
              secondaryValue: formatMoMGrowth(newSignupsChangeRate),
            }
          : {}),
        icon: <PersonAdd sx={{ fontSize: 26, color: '#2563eb' }} />,
        iconBg: 'rgba(37,99,235,0.10)',
        trendText: '최근 7일',
        trendColor: '#2563eb',
        trendBg: 'rgba(37,99,235,0.10)',
        subText: '신규 계약·가입',
        trendIcon: <PersonAdd sx={{ fontSize: 16 }} />,
      },
      {
        label: '계약 만료 임박',
        value: formatNumber(expiringContractCount || 0),
        icon: <Warning sx={{ fontSize: 26, color: '#E73908' }} />,
        iconBg: 'rgba(231,57,8,0.10)',
        trendText: '주의',
        trendIcon: <PriorityHigh sx={{ fontSize: 16 }} />,
        trendColor: '#E73908',
        trendBg: 'rgba(231,57,8,0.10)',
        subText: '갱신 검토 필요',
      },
    ],
    [
      totalCompanyCount,
      newSignupsLast7Days,
      newSignupsChangeRate,
      expiringContractCount,
      activeSiteCount,
    ]
  );

  const systemKpiCards = useMemo(() => {
    const { changeRate, todayCount, priorCount } = dauDayOverDay;
    const up = Number.isFinite(changeRate) && changeRate > 0;
    const down = Number.isFinite(changeRate) && changeRate < 0;
    const dauTrendColor = up ? '#078838' : down ? '#c2410c' : '#64748b';
    const dauTrendBg = up
      ? 'rgba(7,136,56,0.10)'
      : down
        ? 'rgba(194,65,12,0.10)'
        : 'rgba(100,116,139,0.10)';
    return [
      {
        label: '현재 접속자 수',
        value: formatNumber(currentConcurrentUsers || 0),
        icon: <People sx={{ fontSize: 26, color: '#0d9488' }} />,
        iconBg: 'rgba(13,148,136,0.10)',
        trendText: '최근 24시간',
        trendColor: '#0d9488',
        trendBg: 'rgba(13,148,136,0.10)',
        subText: 'FCM 토큰 갱신 기준(회원)',
        trendIcon: <People sx={{ fontSize: 16 }} />,
      },
      {
        label: 'DAU 전일 대비',
        value: formatMoMGrowth(changeRate),
        icon: <ShowChart sx={{ fontSize: 26, color: '#137fec' }} />,
        iconBg: 'rgba(19,127,236,0.08)',
        trendText: `어제 ${formatNumber(priorCount)} · 오늘 ${formatNumber(todayCount)}`,
        trendIcon: down ? (
          <TrendingDown sx={{ fontSize: 16 }} />
        ) : (
          <TrendingUp sx={{ fontSize: 16 }} />
        ),
        trendColor: dauTrendColor,
        trendBg: dauTrendBg,
        subText: '일별 활성 사용자(막대 최근 2일)',
      },
      {
        label: '최근 24시간 API 호출',
        value: formatNumber(apiCallCountLast24Hours || 0),
        icon: <Api sx={{ fontSize: 26, color: '#6366f1' }} />,
        iconBg: 'rgba(99,102,241,0.10)',
        trendText: '요청 수',
        trendColor: '#6366f1',
        trendBg: 'rgba(99,102,241,0.10)',
        subText: 'PROC_ACTION_LOG · 최근 24시간',
        trendIcon: <Api sx={{ fontSize: 16 }} />,
      },
    ];
  }, [currentConcurrentUsers, dauDayOverDay, apiCallCountLast24Hours]);

  const riskListRaw =
    Array.isArray(riskAccounts) && riskAccounts.length
      ? riskAccounts
      : defaultRiskAccounts;

  const riskList = useMemo(() => {
    const parseDaysFromText = (text) => {
      const s = String(text ?? '');
      const m = s.match(/(\d+)\s*일/);
      return m ? Number(m[1]) : null;
    };

    const normalized = (riskListRaw || []).map((r) => {
      const inactiveDays = Number.isFinite(Number(r?.inactiveDays))
        ? Number(r.inactiveDays)
        : (parseDaysFromText(r?.lastLoginText) ?? null);

      return {
        companyName: r?.companyName ?? '',
        inactiveDays,
        riskTag: r?.riskTag ?? r?.level ?? '',
        // fallback 표시용 텍스트 (inactiveDays가 없을 경우 기존 문자열 사용)
        lastLoginText: r?.lastLoginText ?? '',
      };
    });

    // 오래된(비활성일수 큰) 순으로 정렬. inactiveDays가 없으면 뒤로 보냄.
    normalized.sort((a, b) => {
      const ad = a.inactiveDays;
      const bd = b.inactiveDays;
      if (ad == null && bd == null) return 0;
      if (ad == null) return 1;
      if (bd == null) return -1;
      return bd - ad;
    });

    return normalized;
  }, [riskListRaw]);

  // ✅ 화면에는 오래된 3개만
  const riskPreview = useMemo(() => riskList.slice(0, 3), [riskList]);

  const auditList =
    Array.isArray(recentAuditLogs) && recentAuditLogs.length
      ? recentAuditLogs
      : defaultAuditLogs;

  const auditListSorted = useMemo(() => {
    const normalized = (auditList || []).map((r, idx) => {
      const dateValue =
        r?.actionDt ??
        r?.dateText ??
        r?.date ??
        r?.createdAt ??
        r?.createdDt ??
        null;
      const parsed = tryParseDate(dateValue);
      return {
        ...r,
        __idx: idx,
        __parsedTime: parsed ? parsed.getTime() : null,
      };
    });

    // 최신(시간 큰) 순. 날짜가 없으면 뒤로 보냄.
    normalized.sort((a, b) => {
      const at = a.__parsedTime;
      const bt = b.__parsedTime;
      if (at == null && bt == null) return a.__idx - b.__idx;
      if (at == null) return 1;
      if (bt == null) return -1;
      return bt - at;
    });

    return normalized;
  }, [auditList]);

  // ✅ 화면에는 최신 10개만
  const auditPreview = useMemo(
    () => auditListSorted.slice(0, 10),
    [auditListSorted]
  );

  const renderKpiCards = (items, lgCols) =>
    items.map((c) => (
      <Grid key={c.label} item xs={12} sm={6} lg={lgCols}>
        <Card sx={kpiCardSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'text.secondary',
                }}
              >
                {c.label}
              </Typography>
              <Typography sx={{ mt: 1, fontSize: '2rem', fontWeight: 900 }}>
                {c.value}
              </Typography>
              {c.secondaryValue ? (
                <Typography
                  sx={{
                    mt: 0.4,
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'text.secondary',
                  }}
                >
                  {c.secondaryLabel ? `${c.secondaryLabel}: ` : ''}
                  {c.secondaryValue}
                </Typography>
              ) : null}
            </Box>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: c.iconBg,
              }}
            >
              {c.icon}
            </Box>
          </Box>
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                ...pillSx,
                bgcolor: c.trendBg,
                color: c.trendColor,
              }}
            >
              {c.trendIcon ? c.trendIcon : <TrendingUp sx={{ fontSize: 16 }} />}
              {c.trendText}
            </Box>
            <Typography
              sx={{
                ml: 1,
                fontSize: 12,
                color: 'text.secondary',
                fontWeight: 700,
              }}
            >
              {c.subText}
            </Typography>
          </Box>
        </Card>
      </Grid>
    ));

  return (
    <Box
      sx={{
        p: 3,
        minHeight: '100%',
        width: '100%',
        bgcolor: 'rgba(241,245,249,0.85)',
      }}
    >
      <Box sx={{ width: '100%' }}>
        {/* 헤더 영역(페이지 내부) */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 900 }}>
              대시보드 개요
            </Typography>
            <Typography
              sx={{ mt: 0.5, color: 'text.secondary', fontWeight: 500 }}
            >
              환영합니다. 오늘의 현황을 확인하세요.
            </Typography>
          </Box>
        </Box>

        {/* —— 영역: 매출 —— */}
        <DashboardSection
          title="매출 · 계약 매출"
          description="전체·신규·만료 리스크·전년 동월(YoY)·전월 대비와 월별 추이, 상위 고객 비중을 확인합니다."
        >
          <Grid container spacing={2.5} sx={{ mb: 2 }}>
            {renderKpiCards(revenueKpiCards, 4)}
          </Grid>
          <Card sx={{ ...kpiCardSx, p: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
                  월별 계약 회사 수 · 월 계약금 합계
                </Typography>
                <Typography
                  sx={{
                    mt: 0.3,
                    fontSize: 13,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  계약시작일이 해당 월에 속하는 회사 수와, 그 월정액 합계(최근
                  12개월)
                </Typography>
                {!hasMonthlyContractFromApi && (
                  <Typography
                    sx={{
                      mt: 0.75,
                      fontSize: 12,
                      color: 'warning.main',
                      fontWeight: 600,
                    }}
                  >
                    API에 monthlyContractStats·monthlyContractTrend가 없어 최근
                    12개월 축만 표시됩니다. 백엔드 연동 후 데이터가 채워집니다.
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                height: 340,
                borderRadius: 2,
                border: '1px dashed rgba(148,163,184,0.5)',
                bgcolor: 'rgba(15, 23, 42, 0.02)',
                p: 1,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={monthlyContractChartData}
                  margin={{ left: 4, right: 12, top: 8, bottom: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(148,163,184,0.35)"
                  />
                  <XAxis
                    dataKey="monthLabel"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) =>
                      typeof v === 'string' && v.length >= 7 ? v.slice(2) : v
                    }
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 10 }}
                    allowDecimals={false}
                    tickFormatter={(v) => formatNumber(v)}
                    label={{
                      value: '회사 수',
                      angle: -90,
                      position: 'insideLeft',
                      style: { fontSize: 10, fill: '#64748b' },
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => {
                      if (v >= 100000000)
                        return `${(v / 100000000).toFixed(1)}억`;
                      if (v >= 10000)
                        return `${formatNumber(Math.round(v / 10000))}만`;
                      return formatNumber(v);
                    }}
                    label={{
                      value: '계약금(원)',
                      angle: 90,
                      position: 'insideRight',
                      style: { fontSize: 10, fill: '#64748b' },
                    }}
                  />
                  <ReTooltip
                    formatter={(value, name) => {
                      if (name === 'monthlyFeeTotal')
                        return [`${formatNumber(value)}원`, '월 계약금 합계'];
                      if (name === 'companyCount')
                        return [`${formatNumber(value)}사`, '계약 회사 수'];
                      return [value, name];
                    }}
                    labelFormatter={(label) => `월: ${label}`}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                    formatter={(value) =>
                      value === 'monthlyFeeTotal'
                        ? '월 계약금 합계'
                        : value === 'companyCount'
                          ? '계약 회사 수'
                          : value
                    }
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="monthlyFeeTotal"
                    name="monthlyFeeTotal"
                    fill="#0f766e"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="companyCount"
                    name="companyCount"
                    stroke="#137fec"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#137fec' }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Card>

          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            <Grid item xs={12} lg={5}>
              <Card sx={{ ...kpiCardSx, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
                  상위 고객 월정액 비중
                </Typography>
                <Typography
                  sx={{
                    mt: 0.3,
                    mb: 1.5,
                    fontSize: 13,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  Top 8 · 월정액 기준 비율(백엔드 또는 합계 대비 산출)
                </Typography>
                {topSharePieData.length > 0 ? (
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={topSharePieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={58}
                          outerRadius={88}
                          paddingAngle={2}
                        >
                          {topSharePieData.map((entry, index) => (
                            <Cell
                              key={`top-share-${index}-${entry.fullName}`}
                              fill={
                                TOP_REVENUE_SHARE_PIE_COLORS[
                                  index % TOP_REVENUE_SHARE_PIE_COLORS.length
                                ]
                              }
                            />
                          ))}
                        </Pie>
                        <ReTooltip
                          formatter={(v, _n, p) => {
                            const fee = p?.payload?.fee;
                            return [
                              `${v}% · ${formatNumber(fee)}원`,
                              p?.payload?.fullName ?? '',
                            ];
                          }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: 11 }}
                          formatter={(value) => value}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: 'center',
                      color: 'text.secondary',
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    API에 topCustomerRevenueShares가 없습니다. 백엔드 연동 후
                    표시됩니다.
                  </Box>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} lg={7}>
              <Card
                sx={{ ...kpiCardSx, p: 0, height: '100%', overflow: 'hidden' }}
              >
                <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
                    상위 고객 매출(월정액)
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.3,
                      fontSize: 13,
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    기업명 · 금액 · 비중
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(15,23,42,0.02)' }}>
                      <TableCell sx={{ fontWeight: 900, fontSize: 12 }}>
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: 900, fontSize: 12 }}>
                        기업명
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 900, fontSize: 12 }}
                      >
                        월정액
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 900, fontSize: 12 }}
                      >
                        비중
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topCustomerRevenueShares.length > 0 ? (
                      (() => {
                        const sum = topCustomerRevenueShares.reduce(
                          (s, r) => s + (Number(r.monthlyFee) || 0),
                          0
                        );
                        return topCustomerRevenueShares
                          .slice(0, 12)
                          .map((r) => {
                            const fee = Number(r.monthlyFee) || 0;
                            const pct = Number.isFinite(Number(r.sharePercent))
                              ? Number(r.sharePercent)
                              : sum > 0
                                ? (fee / sum) * 100
                                : 0;
                            return (
                              <TableRow
                                key={`${r.rank}-${r.companyName}`}
                                hover
                              >
                                <TableCell sx={{ fontSize: 13 }}>
                                  {r.rank}
                                </TableCell>
                                <TableCell
                                  sx={{ fontSize: 13, fontWeight: 700 }}
                                >
                                  {r.companyName}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: 13 }}>
                                  {formatNumber(fee)}원
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: 13 }}>
                                  {Number.isFinite(pct)
                                    ? `${pct.toFixed(1)}%`
                                    : '—'}
                                </TableCell>
                              </TableRow>
                            );
                          });
                      })()
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography
                            sx={{
                              color: 'text.secondary',
                              fontWeight: 600,
                              fontSize: 13,
                            }}
                          >
                            데이터가 없습니다. R1에 topCustomerRevenueShares
                            배열을 내려주세요.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>
            </Grid>
          </Grid>
        </DashboardSection>

        <DashboardSection
          title="고객 상태"
          description="고객사·신규 가입·만료 임박과 활성 사업장 규모를 한눈에 봅니다."
        >
          <Grid container spacing={2.5}>
            {renderKpiCards(customerKpiCards, 3)}
          </Grid>
        </DashboardSection>

        <DashboardSection
          title="위험·비활성 알림"
          description="장기 미접속 등 위험 신호가 있는 고객사입니다."
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    bgcolor: 'rgba(15,23,42,0.02)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid rgba(231,237,243,1)',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
                      위험 계정
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: 12,
                        color: 'text.secondary',
                        fontWeight: 600,
                      }}
                    >
                      14일 이상 비활성 기업
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: 999,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'rgba(231,57,8,0.10)',
                      color: '#E73908',
                      fontWeight: 900,
                      fontSize: 12,
                    }}
                  >
                    {riskList.length}
                  </Box>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          기업명
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          마지막 로그인
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {riskPreview.map((r) => {
                        const level = r.riskTag ?? '';
                        const isHigh = level === '고위험';
                        const companyName = r.companyName ?? '';
                        const lastLoginText = Number.isFinite(
                          Number(r.inactiveDays)
                        )
                          ? `${r.inactiveDays}일 전`
                          : (r.lastLoginText ?? '');
                        return (
                          <TableRow key={companyName} hover>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography
                                sx={{ fontSize: 13, fontWeight: 900 }}
                              >
                                {companyName}
                              </Typography>
                              <Box sx={{ mt: 0.75 }}>
                                <Box
                                  sx={{
                                    ...pillSx,
                                    bgcolor: isHigh
                                      ? 'rgba(231,57,8,0.08)'
                                      : 'rgba(255,183,77,0.18)',
                                    color: isHigh ? '#E73908' : '#E68A00',
                                    border: `1px solid ${
                                      isHigh
                                        ? 'rgba(231,57,8,0.10)'
                                        : 'rgba(255,183,77,0.25)'
                                    }`,
                                  }}
                                >
                                  {level}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5 }}>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 800,
                                  color: 'text.secondary',
                                }}
                              >
                                {lastLoginText}
                              </Typography>
                              <Button
                                size="small"
                                startIcon={
                                  <Notifications sx={{ fontSize: 16 }} />
                                }
                                sx={{
                                  mt: 0.25,
                                  fontSize: 12,
                                  fontWeight: 900,
                                  color: '#137fec',
                                  textTransform: 'none',
                                }}
                              >
                                알림 보내기
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>

                <Divider />
                <Box
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    bgcolor: 'rgba(15,23,42,0.02)',
                  }}
                >
                  <Button
                    endIcon={<ArrowForward sx={{ fontSize: 18 }} />}
                    sx={{
                      fontWeight: 900,
                      color: '#137fec',
                      textTransform: 'none',
                    }}
                    onClick={() => setRiskAllOpen(true)}
                  >
                    위험 계정 전체 보기
                  </Button>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </DashboardSection>

        <DashboardSection
          title="트래픽 추이"
          description="일별 활성 사용자는 선택한 기간 기준이며, 요일·시간대·기기 유형은 같은 행에서 비교합니다. 요일·시간은 최근 30일 PROC_ACTION_LOG 고유 사용자, 기기는 USE_YN=Y 회원 FCM(TB_BS_MEMBER_FCM) 건수입니다."
        >
          <Grid container spacing={2.5} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
                      일자별 활성 사용자
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.3,
                        fontSize: 13,
                        color: 'text.secondary',
                        fontWeight: 600,
                      }}
                    >
                      지난 {periodDays}일간 일별 활성 사용자 추이 · 주황선은 7일
                      이동평균
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          bgcolor: '#137fec',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 800,
                          color: 'text.secondary',
                        }}
                      >
                        일별 활성
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 3,
                          borderRadius: 1,
                          bgcolor: '#c2410c',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 800,
                          color: 'text.secondary',
                        }}
                      >
                        7일 이동평균
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    height: 320,
                    borderRadius: 2,
                    border: '1px dashed rgba(148,163,184,0.6)',
                    bgcolor: 'rgba(15, 23, 42, 0.02)',
                    p: 2,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={dailyActiveUsersChartData}
                      margin={{ left: 8, right: 8, top: 10, bottom: 10 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(148,163,184,0.25)"
                      />
                      <XAxis dataKey="dateLabel" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <ReTooltip
                        formatter={(value, name) => {
                          const label =
                            name === '7일 이동평균'
                              ? '7일 이동평균'
                              : '일별 활성';
                          return [`${formatNumber(value)}명`, label];
                        }}
                        labelFormatter={(l) => `일자: ${l}`}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                        formatter={(value) => value}
                      />
                      <Bar
                        name="일별 활성"
                        dataKey="activeUserCount"
                        fill="#137fec"
                        radius={[6, 6, 0, 0]}
                      />
                      <Line
                        type="monotone"
                        name="7일 이동평균"
                        dataKey="activeUserCountMa7"
                        stroke="#c2410c"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                  height: '100%',
                }}
              >
                <Typography sx={{ fontSize: 17, fontWeight: 900 }}>
                  요일별 접속 분포
                </Typography>
                <Typography
                  sx={{
                    mt: 0.3,
                    mb: 1.5,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  최근 30일, 요일당 고유 사용자 수
                </Typography>
                <Box
                  sx={{
                    height: 260,
                    borderRadius: 2,
                    border: '1px dashed rgba(148,163,184,0.6)',
                    bgcolor: 'rgba(15, 23, 42, 0.02)',
                    p: 2,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={trafficByWeekday}
                      margin={{ left: 4, right: 8, top: 8, bottom: 8 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(148,163,184,0.35)"
                      />
                      <XAxis dataKey="weekdayLabel" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        allowDecimals={false}
                        tickFormatter={(v) => formatNumber(v)}
                      />
                      <ReTooltip
                        formatter={(v) => [
                          `${formatNumber(v)}명`,
                          '고유 사용자',
                        ]}
                        labelFormatter={(l) => `요일: ${l}`}
                      />
                      <Bar
                        dataKey="userCount"
                        fill="#7c3aed"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={40}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                  height: '100%',
                }}
              >
                <Typography sx={{ fontSize: 17, fontWeight: 900 }}>
                  시간대별 접속 분포
                </Typography>
                <Typography
                  sx={{
                    mt: 0.3,
                    mb: 1.5,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  최근 30일, 시(0~23)당 고유 사용자 수
                </Typography>
                <Box
                  sx={{
                    height: 260,
                    borderRadius: 2,
                    border: '1px dashed rgba(148,163,184,0.6)',
                    bgcolor: 'rgba(15, 23, 42, 0.02)',
                    p: 2,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={trafficByHour}
                      margin={{ left: 4, right: 8, top: 8, bottom: 44 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(148,163,184,0.35)"
                      />
                      <XAxis
                        dataKey="hourLabel"
                        tick={{ fontSize: 9 }}
                        interval={2}
                        angle={-32}
                        textAnchor="end"
                        height={40}
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        allowDecimals={false}
                        tickFormatter={(v) => formatNumber(v)}
                      />
                      <ReTooltip
                        formatter={(v) => [
                          `${formatNumber(v)}명`,
                          '고유 사용자',
                        ]}
                        labelFormatter={(l) => `시간: ${l}`}
                      />
                      <Bar
                        dataKey="userCount"
                        fill="#0d9488"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={22}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                  height: '100%',
                }}
              >
                <Typography sx={{ fontSize: 17, fontWeight: 900 }}>
                  기기 유형별 사용량
                </Typography>
                <Typography
                  sx={{
                    mt: 0.3,
                    mb: 1.5,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  FCM 등록 건수 · USE_YN=Y
                </Typography>
                <Box
                  sx={{
                    height: 260,
                    borderRadius: 2,
                    border: '1px dashed rgba(148,163,184,0.6)',
                    bgcolor: 'rgba(15, 23, 42, 0.02)',
                    p: 1.5,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={deviceTypeUsages}
                      margin={{ left: 2, right: 4, top: 6, bottom: 6 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(148,163,184,0.35)"
                      />
                      <XAxis dataKey="displayLabel" tick={{ fontSize: 11 }} />
                      <YAxis
                        width={36}
                        tick={{ fontSize: 9 }}
                        allowDecimals={false}
                        tickFormatter={(v) => formatNumber(v)}
                      />
                      <ReTooltip
                        formatter={(v) => [`${formatNumber(v)}건`, '등록 건수']}
                        labelFormatter={(l) => `기기: ${l}`}
                      />
                      <Bar
                        dataKey="usageCount"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={44}
                      >
                        {deviceTypeUsages.map((_, index) => (
                          <Cell
                            key={`device-bar-${index}`}
                            fill={
                              DEVICE_TYPE_CHART_COLORS[
                                index % DEVICE_TYPE_CHART_COLORS.length
                              ]
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </DashboardSection>

        <DashboardSection
          title="사용량 분석"
          description="최근 30일 PROC_ACTION_LOG(화면 저장 INSERT/UPDATE/DELETE) 기준입니다. 웹 화면 메뉴(SCREEN_TYPE=0) 기준 TOP 5입니다."
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
                      회사별 사용 현황
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.35,
                        fontSize: 12,
                        color: 'text.secondary',
                        fontWeight: 600,
                      }}
                    >
                      최근 30일 · 활용률 = 활성 사용자 ÷ 계약 인원 · 높은 순
                    </Typography>
                  </Box>
                </Box>

                {companyUsageChartRows.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    {[
                      {
                        label: '전체',
                        value: `${formatNumber(companyUsageOverview.total)}개사`,
                        sub: '유효 계약',
                        bgcolor: 'rgba(19,127,236,0.08)',
                      },
                      {
                        label: '평균 활용률',
                        value: `${companyUsageOverview.avgRate}%`,
                        sub: '전체 회사',
                        bgcolor: 'rgba(15,23,42,0.04)',
                      },
                      {
                        label: '양호',
                        value: `${formatNumber(companyUsageOverview.goodCount)}개사`,
                        sub: '70% 이상',
                        bgcolor: 'rgba(34,197,94,0.10)',
                        color: '#15803d',
                      },
                      {
                        label: '주의',
                        value: `${formatNumber(companyUsageOverview.warnCount)}개사`,
                        sub: '30~69%',
                        bgcolor: 'rgba(234,179,8,0.12)',
                        color: '#B45309',
                      },
                      {
                        label: '저활용',
                        value: `${formatNumber(companyUsageOverview.lowCount)}개사`,
                        sub: '30% 미만',
                        bgcolor: 'rgba(239,68,68,0.10)',
                        color: '#E73908',
                      },
                    ].map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          flex: '1 1 100px',
                          minWidth: 100,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: item.bgcolor,
                          border: '1px solid rgba(231,237,243,0.9)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: item.color || 'text.secondary',
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 18,
                            fontWeight: 900,
                            mt: 0.25,
                            color: item.color || 'text.primary',
                          }}
                        >
                          {item.value}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: 'text.secondary',
                            fontWeight: 600,
                            mt: 0.15,
                          }}
                        >
                          {item.sub}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    border: '1px solid rgba(231,237,243,1)',
                    bgcolor: 'rgba(15, 23, 42, 0.02)',
                    p: { xs: 1.5, sm: 2 },
                    maxHeight: 620,
                    overflowY: 'auto',
                  }}
                >
                  {companyUsageChartRows.length === 0 ? (
                    <Box
                      sx={{
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      표시할 기업 데이터가 없습니다.
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          height: 28,
                          borderRadius: 1,
                          overflow: 'hidden',
                          mb: 1.5,
                          border: '1px solid rgba(148,163,184,0.35)',
                        }}
                      >
                        {COMPANY_USAGE_ZONE_BANDS.map((zone) => (
                          <Box
                            key={zone.label}
                            sx={{
                              width: `${zone.widthPct}%`,
                              bgcolor: zone.bgcolor,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRight:
                                zone.label !== '양호'
                                  ? '1px dashed rgba(148,163,184,0.45)'
                                  : 'none',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 800,
                                color: 'text.secondary',
                              }}
                            >
                              {zone.label}
                              {zone.label === '저활용' ? ' · 0~29%' : ''}
                              {zone.label === '주의' ? ' · 30~69%' : ''}
                              {zone.label === '양호' ? ' · 70~100%' : ''}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {companyUsageChartRows.map((row) => (
                        <Box
                          key={row.coCd || row.companyFullName}
                          sx={{
                            py: 1.25,
                            borderBottom: '1px solid rgba(231,237,243,0.9)',
                            '&:last-of-type': { borderBottom: 'none' },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 1,
                              mb: 0.75,
                            }}
                          >
                            <Box sx={{ minWidth: 0, flex: '1 1 180px' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  alignItems: 'center',
                                  gap: 0.75,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: { xs: 13, sm: 14 },
                                    fontWeight: 900,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {row.companyFullName}
                                </Typography>
                                <Box
                                  sx={{
                                    ...pillSx,
                                    ...row.statusPillSx,
                                    fontSize: 11,
                                    py: 0.15,
                                  }}
                                >
                                  {row.statusLabel}
                                </Box>
                              </Box>
                              <Typography
                                sx={{
                                  mt: 0.35,
                                  fontSize: 11,
                                  color: 'text.secondary',
                                  fontWeight: 600,
                                }}
                              >
                                {row.memberSummary} · 액션{' '}
                                {formatNumber(row.actionCount)}건
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                fontSize: 22,
                                fontWeight: 900,
                                color: row.barFill,
                                lineHeight: 1,
                                flexShrink: 0,
                              }}
                            >
                              {row.usageRatePercent}%
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              position: 'relative',
                              height: 14,
                              borderRadius: 999,
                              bgcolor: 'rgba(148,163,184,0.25)',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${Math.min(100, Math.max(0, row.usageRatePercent))}%`,
                                height: '100%',
                                bgcolor: row.barFill,
                                borderRadius: 999,
                                transition: 'width 0.3s ease',
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                left: '30%',
                                top: 0,
                                bottom: 0,
                                width: 1,
                                bgcolor: 'rgba(0,0,0,0.08)',
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                left: '70%',
                                top: 0,
                                bottom: 0,
                                width: 1,
                                bgcolor: 'rgba(0,0,0,0.08)',
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6}>
              <TopMenuFeatureTop5Card
                title="가장 많이 사용되는 기능 TOP 5"
                subtitle="웹 화면 메뉴(SCREEN_TYPE=0) · 저장 액션 건수 · 최근 30일"
                accentColor="#137fec"
                headerBg="rgba(19,127,236,0.04)"
                rows={topMenuWeb}
                emptyMessage="표시할 웹 이용 데이터가 없습니다."
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: '1px solid rgba(231,237,243,1)',
                    bgcolor: 'rgba(19,127,236,0.04)',
                  }}
                >
                  <Typography sx={{ fontSize: 17, fontWeight: 900 }}>
                    활발 사용 TOP 5 기업
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.35,
                      fontSize: 12,
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    웹 화면 액션 건수 · 활성 사용자 · 활용률(%)
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(15,23,42,0.02)' }}>
                        <TableCell
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                            width: 48,
                          }}
                        >
                          순위
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          기업명
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          활성 사용자
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          액션
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          활용률
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topActiveCompanies.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{ py: 4, color: 'text.secondary' }}
                          >
                            표시할 데이터가 없습니다.
                          </TableCell>
                        </TableRow>
                      ) : (
                        topActiveCompanies.map((row) => {
                          const rate = Number(row.usageRatePercent) || 0;
                          const rateStyle =
                            rate >= 70
                              ? {
                                  bgcolor: 'rgba(34,197,94,0.12)',
                                  color: '#15803d',
                                  border: '1px solid rgba(34,197,94,0.25)',
                                }
                              : rate >= 30
                                ? {
                                    bgcolor: 'rgba(255,183,77,0.18)',
                                    color: '#E68A00',
                                    border: '1px solid rgba(255,183,77,0.25)',
                                  }
                                : {
                                    bgcolor: 'rgba(231,57,8,0.08)',
                                    color: '#E73908',
                                    border: '1px solid rgba(231,57,8,0.12)',
                                  };
                          return (
                            <TableRow
                              key={row.coCd || row.companyName}
                              hover
                            >
                              <TableCell sx={{ fontWeight: 900 }}>
                                {row.rank}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>
                                {row.companyName}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(row.activeUserCount)}명
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(row.actionCount)}건
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ ...pillSx, ...rateStyle }}>
                                  {rate}%
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: '1px solid rgba(231,237,243,1)',
                    bgcolor: 'rgba(231,57,8,0.04)',
                  }}
                >
                  <Typography sx={{ fontSize: 17, fontWeight: 900 }}>
                    저활용 기업 목록
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.35,
                      fontSize: 12,
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    활용률 30% 미만 · 최대 20개사 (낮은 순)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    maxHeight: { xs: 320, lg: 360 },
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(15,23,42,0.02)' }}>
                        <TableCell
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          기업명
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          활성 사용자
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          액션
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          활용률
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lowUsageCompanies.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ py: 4, color: 'text.secondary' }}
                          >
                            저활용으로 분류된 기업이 없습니다.
                          </TableCell>
                        </TableRow>
                      ) : (
                        lowUsageCompanies.map((row) => (
                          <TableRow
                            key={row.coCd || row.companyName}
                            hover
                          >
                            <TableCell sx={{ fontWeight: 800 }}>
                              {row.companyName}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(row.activeUserCount)}명
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(row.actionCount)}건
                            </TableCell>
                            <TableCell align="right">
                              <Box
                                sx={{
                                  ...pillSx,
                                  bgcolor: 'rgba(231,57,8,0.08)',
                                  color: '#E73908',
                                  border: '1px solid rgba(231,57,8,0.12)',
                                }}
                              >
                                {Number(row.usageRatePercent) || 0}%
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </DashboardSection>

        <DashboardSection
          title="이탈률 분석"
          description="PROC_ACTION_LOG 기준입니다. 세션은 동일 사용자·회사에서 액션 간격 30분 초과 시 새 세션으로 보고, 페이지 이탈률은 해당 메뉴에서 저장 액션이 1회만 발생한 사용자 비율(웹 메뉴, 방문 3명 이상)입니다."
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={5}>
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                  height: '100%',
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
                  세션 지속 시간 비교
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  평균 세션 길이 · 최근 30일 vs 직전 30일
                </Typography>

                {!sessionDurationComparison ||
                (sessionDurationChartRows[0]?.durationSeconds === 0 &&
                  sessionDurationChartRows[1]?.durationSeconds === 0) ? (
                  <Box
                    sx={{
                      height: 260,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    표시할 세션 데이터가 없습니다.
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        mt: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          flex: '1 1 140px',
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: 'rgba(148,163,184,0.12)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: 'text.secondary',
                          }}
                        >
                          직전 30일
                        </Typography>
                        <Typography sx={{ fontSize: 20, fontWeight: 900, mt: 0.5 }}>
                          {FormatSessionDuration(
                            sessionDurationComparison.priorAvgDurationSeconds
                          )}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: 'text.secondary',
                            fontWeight: 600,
                            mt: 0.25,
                          }}
                        >
                          세션 {formatNumber(sessionDurationComparison.priorSessionCount)}건
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: '1 1 140px',
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: 'rgba(19,127,236,0.08)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: 'text.secondary',
                          }}
                        >
                          최근 30일
                        </Typography>
                        <Typography sx={{ fontSize: 20, fontWeight: 900, mt: 0.5 }}>
                          {FormatSessionDuration(
                            sessionDurationComparison.currentAvgDurationSeconds
                          )}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: 'text.secondary',
                            fontWeight: 600,
                            mt: 0.25,
                          }}
                        >
                          세션 {formatNumber(sessionDurationComparison.currentSessionCount)}건
                        </Typography>
                      </Box>
                    </Box>

                    {Number.isFinite(sessionDurationChangeRate) && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mb: 1.5,
                          px: 1.25,
                          py: 0.5,
                          borderRadius: 999,
                          bgcolor:
                            sessionDurationChangeRate > 0
                              ? 'rgba(34,197,94,0.12)'
                              : sessionDurationChangeRate < 0
                                ? 'rgba(231,57,8,0.08)'
                                : 'rgba(148,163,184,0.15)',
                          color:
                            sessionDurationChangeRate > 0
                              ? '#15803d'
                              : sessionDurationChangeRate < 0
                                ? '#E73908'
                                : 'text.secondary',
                        }}
                      >
                        {sessionDurationChangeRate > 0 ? (
                          <TrendingUp sx={{ fontSize: 16 }} />
                        ) : sessionDurationChangeRate < 0 ? (
                          <TrendingDown sx={{ fontSize: 16 }} />
                        ) : null}
                        <Typography sx={{ fontSize: 12, fontWeight: 900 }}>
                          직전 대비 {formatMoMGrowth(sessionDurationChangeRate)}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={sessionDurationChartRows}
                          margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="rgba(148,163,184,0.25)"
                          />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis
                            tick={{ fontSize: 10 }}
                            tickFormatter={(v) =>
                              v >= 60 ? `${Math.round(v / 60)}분` : `${v}초`
                            }
                          />
                          <ReTooltip
                            formatter={(value) => [
                              FormatSessionDuration(value),
                              '평균 지속',
                            ]}
                          />
                          <Bar dataKey="durationSeconds" radius={[6, 6, 0, 0]}>
                            {sessionDurationChartRows.map((entry) => (
                              <Cell key={entry.label} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: '1px solid rgba(231,237,243,1)',
                    bgcolor: 'rgba(231,57,8,0.04)',
                  }}
                >
                  <Typography sx={{ fontSize: 17, fontWeight: 900 }}>
                    페이지 이탈률 (상위 10)
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.35,
                      fontSize: 12,
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    웹 메뉴 · 단일 저장 액션만 한 사용자 비율이 높은 순
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(15,23,42,0.02)' }}>
                        <TableCell
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                            width: 48,
                          }}
                        >
                          순위
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          페이지
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          방문
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          이탈
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: 'text.secondary',
                          }}
                        >
                          이탈률
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageBounceRates.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{ py: 4, color: 'text.secondary' }}
                          >
                            표시할 이탈률 데이터가 없습니다.
                          </TableCell>
                        </TableRow>
                      ) : (
                        pageBounceRates.map((row) => (
                          <TableRow
                            key={row.menuId || row.pageName}
                            hover
                          >
                            <TableCell sx={{ fontWeight: 900 }}>
                              {row.rank}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>
                              {row.pageName}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(row.visitorCount)}명
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(row.bounceVisitorCount)}명
                            </TableCell>
                            <TableCell align="right">
                              <Box
                                sx={{
                                  ...pillSx,
                                  ...GetBounceRateStyle(row.bounceRatePercent),
                                }}
                              >
                                {Number(row.bounceRatePercent) || 0}%
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </DashboardSection>

        {/* 위험 계정 전체 보기 모달 */}
        <Dialog
          open={riskAllOpen}
          onClose={() => setRiskAllOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ fontWeight: 900, pr: 6 }}>
            위험 계정 전체 보기
            <IconButton
              onClick={() => setRiskAllOpen(false)}
              sx={{ position: 'absolute', right: 12, top: 10 }}
              aria-label="close"
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(15,23,42,0.02)' }}>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    기업명
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    위험도
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    비활성 기간
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {riskList.map((r) => {
                  const level = r.riskTag ?? '';
                  const isHigh = level === '고위험';
                  const inactiveText = Number.isFinite(Number(r.inactiveDays))
                    ? `${r.inactiveDays}일`
                    : '-';

                  return (
                    <TableRow key={`all-${r.companyName}`} hover>
                      <TableCell sx={{ py: 1.5, fontWeight: 900 }}>
                        {r.companyName}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Box
                          sx={{
                            ...pillSx,
                            bgcolor: isHigh
                              ? 'rgba(231,57,8,0.08)'
                              : 'rgba(255,183,77,0.18)',
                            color: isHigh ? '#E73908' : '#E68A00',
                            border: `1px solid ${
                              isHigh
                                ? 'rgba(231,57,8,0.10)'
                                : 'rgba(255,183,77,0.25)'
                            }`,
                          }}
                        >
                          {level}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          py: 1.5,
                          color: 'text.secondary',
                          fontWeight: 800,
                        }}
                      >
                        {inactiveText}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions sx={{ p: 1.5 }}>
            <Button
              onClick={() => setRiskAllOpen(false)}
              variant="contained"
              sx={{
                bgcolor: '#137fec',
                fontWeight: 900,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#0f6dd1', boxShadow: 'none' },
              }}
            >
              닫기
            </Button>
          </DialogActions>
        </Dialog>

        <DashboardSection
          title="시스템 부하"
          description="접속·DAU 추이와 최근 24시간 API 호출량으로 부하를 점검합니다."
        >
          <Grid container spacing={2.5}>
            {renderKpiCards(systemKpiCards, 4)}
          </Grid>
        </DashboardSection>

        {/* 감사 로그 */}
        <Card
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(231,237,243,1)',
            boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(231,237,243,1)',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
                최근 감사 로그
              </Typography>
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: 12,
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                기업 관리자의 최근 시스템 작업
              </Typography>
            </Box>
            <Button
              sx={{
                fontWeight: 900,
                color: 'text.secondary',
                textTransform: 'none',
              }}
              onClick={() => setAuditAllOpen(true)}
            >
              전체 로그 보기
            </Button>
          </Box>

          <Box sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(15,23,42,0.02)' }}>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    사용자
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    작업
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    기업
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    날짜
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    상태
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditPreview.map((r, idx) => {
                  const statusRaw = r.actionResult ?? r.status ?? '';
                  const ok = statusRaw === 'SUCCESS' || statusRaw === '성공';
                  const statusText =
                    statusRaw === 'SUCCESS'
                      ? '성공'
                      : statusRaw === 'ERROR'
                        ? '오류'
                        : statusRaw || '경고';
                  const user = r.userName ?? r.user ?? '';
                  const action =
                    r.action ??
                    (r.menuId
                      ? `${r.menuId} (${r.actionType || ''})`
                      : r.actionType || '');
                  const company = r.companyName ?? r.company ?? '';
                  const dateText = r.actionDt ?? r.dateText ?? '';
                  return (
                    <TableRow
                      key={`${user}-${r.menuId || ''}-${r.actionDt || r.dateText || ''}-${idx}`}
                      hover
                    >
                      <TableCell sx={{ py: 1.75, fontWeight: 900 }}>
                        {user}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.75,
                          color: 'text.secondary',
                          fontWeight: 700,
                        }}
                      >
                        {action}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.75,
                          color: 'text.secondary',
                          fontWeight: 700,
                        }}
                      >
                        {company}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.75,
                          color: 'text.disabled',
                          fontWeight: 700,
                        }}
                      >
                        {dateText}
                      </TableCell>
                      <TableCell sx={{ py: 1.75 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.2,
                            py: 0.4,
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 900,
                            color: ok ? '#078838' : '#9A6B00',
                            bgcolor: ok
                              ? 'rgba(7,136,56,0.10)'
                              : 'rgba(255,193,7,0.18)',
                          }}
                        >
                          {statusText}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Card>

        {/* 감사 로그 전체 보기 모달 */}
        <Dialog
          open={auditAllOpen}
          onClose={() => setAuditAllOpen(false)}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle sx={{ fontWeight: 900, pr: 6 }}>
            전체 감사 로그
            <IconButton
              onClick={() => setAuditAllOpen(false)}
              sx={{ position: 'absolute', right: 12, top: 10 }}
              aria-label="close"
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(15,23,42,0.02)' }}>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    사용자
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    작업
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    기업
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    날짜
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'text.secondary',
                    }}
                  >
                    상태
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditListSorted.map((r, idx) => {
                  const statusRaw = r.actionResult ?? r.status ?? '';
                  const ok = statusRaw === 'SUCCESS' || statusRaw === '성공';
                  const statusText =
                    statusRaw === 'SUCCESS'
                      ? '성공'
                      : statusRaw === 'ERROR'
                        ? '오류'
                        : statusRaw || '경고';
                  const user = r.userName ?? r.user ?? '';
                  const action =
                    r.action ??
                    (r.menuId
                      ? `${r.menuId} (${r.actionType || ''})`
                      : r.actionType || '');
                  const company = r.companyName ?? r.company ?? '';
                  const dateText = r.actionDt ?? r.dateText ?? '';

                  return (
                    <TableRow
                      key={`all-${user}-${r.menuId || ''}-${r.actionDt || r.dateText || ''}-${idx}`}
                      hover
                    >
                      <TableCell sx={{ py: 1.75, fontWeight: 900 }}>
                        {user}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.75,
                          color: 'text.secondary',
                          fontWeight: 700,
                        }}
                      >
                        {action}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.75,
                          color: 'text.secondary',
                          fontWeight: 700,
                        }}
                      >
                        {company}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.75,
                          color: 'text.disabled',
                          fontWeight: 700,
                        }}
                      >
                        {dateText}
                      </TableCell>
                      <TableCell sx={{ py: 1.75 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.2,
                            py: 0.4,
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 900,
                            color: ok ? '#078838' : '#9A6B00',
                            bgcolor: ok
                              ? 'rgba(7,136,56,0.10)'
                              : 'rgba(255,193,7,0.18)',
                          }}
                        >
                          {statusText}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions sx={{ p: 1.5 }}>
            <Button
              onClick={() => setAuditAllOpen(false)}
              variant="contained"
              sx={{
                bgcolor: '#137fec',
                fontWeight: 900,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#0f6dd1', boxShadow: 'none' },
              }}
            >
              닫기
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default MAI_BASIC;
